import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Indicator, Language, Observation, Score } from 'src/models/models';
import { BaseService } from './base.service';

// Interface for full indicator with nested observations and levels from API
export interface FullIndicatorLevel {
  id: string;
  score: Score;
  text: string;
  observationId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FullIndicatorObservation {
  id: string;
  observation: string;
  indicatorId: string;
  levels: FullIndicatorLevel[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FullIndicatorTranslation {
  id: string;
  translation: string;
  languageId: string;
  language?: Language;
  indicatorId?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// FullIndicator for import/export functionality with properly typed nested data
export interface FullIndicator {
  id: string;
  name: string;
  masterId?: string;
  observations: FullIndicatorObservation[];
  translations: FullIndicatorTranslation[];
  createdAt?: Date;
  updatedAt?: Date;
}

// Interface for import result
export interface ImportResult {
  created: number;
  updated: number;
  errors: string[];
}

// Interface for import data
export interface ImportLevelData {
  score: string | undefined;
  levelText: string;
  levelId?: string;
}

export interface ImportObservationData {
  observationText: string;
  observationId?: string;
  levels: ImportLevelData[];
}

export interface ImportIndicatorData {
  name: string;
  originalId?: string;
  parentName?: string;
  observations: ImportObservationData[];
  translations?: { [langCode: string]: string };
  childs?: ImportIndicatorData[];
}

@Injectable({
  providedIn: 'root'
})
export class IndicatorService extends BaseService<Indicator, Indicator> {

  constructor(protected override http: HttpClient) {
    super(http, 'indicators');
  }

  getMasters(): Observable<Indicator[]> {
    return super.getAll('/getMasters');
  }

  getMastersAndChilds(): Observable<Indicator[]> {
    return super.getAll('/getMastersAndChilds');
  }

  getIndicator(id: string): Observable<Indicator> {
    return super.getCustomType<Indicator>('/' + id);
  }

  addObservation(id: string, observation: Observation): Observable<Observation> {
    const url = this.createUrl(`/${id}/addObservation`);
    return this.http.post<Observation>(url, observation);
  }

  // Returns indicators with full nested data (observations, levels, translations)
  // Use getFullIndicatorsTyped() when you need proper typing for nested properties
  getFullIndicators(): Observable<Indicator[]> {
    return super.getAll('/full');
  }

  // Returns fully typed indicators for import/export functionality
  getFullIndicatorsTyped(): Observable<FullIndicator[]> {
    return super.getAllCustomtype<FullIndicator>('/full');
  }

  import(data: ImportIndicatorData[]): Observable<ImportResult> {
    const url = this.createUrl('/import');
    return this.http.post<ImportResult>(url, { indicators: data });
  }
}
